import React, { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  PhotoIcon,
  EyeSlashIcon,
  EyeIcon,
  CloudArrowUpIcon,
  DocumentIcon,
  ClipboardDocumentListIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InformationCircleIcon,
  SparklesIcon,
  ClockIcon
} from '@heroicons/react/24/outline'
import { uploadRestaurantImage, deleteRestaurantImage, compressRestaurantImage, uploadImageToStorage, uploadImageRLSFree } from '../../utils/storageUtils'
import toast from 'react-hot-toast'
import { useConfirmation } from '../../contexts/ConfirmationContext'
import {
  calculateNutritionDefaults,
  hasCustomNutrition,
  calculatePlatformPrice,
  calculateDefaultDisplayPrice,
  calculateDiscountPercentage,
  getPricingBreakdown,
  validatePricing,
  validateServing,
  formatAllergenName,
  getDefaultServingSize,
  ALLERGEN_OPTIONS,
  SERVING_UNIT_OPTIONS
} from '../../utils/nutritionUtils'

const MenuTab = ({ menuItems, categories, onAddItem, onUpdateItem, onDeleteItem, onAddCategory, restaurantId }) => {
  const { showConfirmation } = useConfirmation()
  const [showAddItemModal, setShowAddItemModal] = useState(false)
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    actual_price: '',
    platform_price: '',
    menu_display_price: '',
    category_id: '',
    serving_size: '',
    serving_unit: 'g',
    preparation_time: '15',
    allergens: [],
    ingredients: [],
    is_vegetarian: false,
    is_vegan: false,
    is_spicy: false,
    nutrition: {
      calories: '',
      protein: '',
      fat: '',
      carbs: '',
      fiber: ''
    },
    is_available: true,
    image_url: ''
  })
  
  // New state for expanded sections and ingredient input
  const [showDishDetails, setShowDishDetails] = useState(false)
  const [ingredientInput, setIngredientInput] = useState('')
  const [editIngredientInput, setEditIngredientInput] = useState('')
  const [editShowDishDetails, setEditShowDishDetails] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const [selectedImage, setSelectedImage] = useState(null)
  const [imagePreview, setImagePreview] = useState('')
  const [uploadingImage, setUploadingImage] = useState(false)
  const [editImagePreview, setEditImagePreview] = useState('')
  const [editSelectedImage, setEditSelectedImage] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isEditDragging, setIsEditDragging] = useState(false)
  const [newCategory, setNewCategory] = useState({ name: '', description: '' })

  // Auto-calculate platform price when actual price changes
  useEffect(() => {
    if (newItem.actual_price && parseFloat(newItem.actual_price) > 0) {
      const platformPrice = calculatePlatformPrice(parseFloat(newItem.actual_price), 2)
      setNewItem(prev => ({ ...prev, platform_price: platformPrice.toString() }))
    }
  }, [newItem.actual_price])

  // Calculate pricing breakdown for preview
  const pricingBreakdown = useMemo(() => {
    if (!newItem.actual_price || parseFloat(newItem.actual_price) <= 0) return null
    return getPricingBreakdown(
      newItem.actual_price,
      newItem.platform_price,
      newItem.menu_display_price,
      2
    )
  }, [newItem.actual_price, newItem.platform_price, newItem.menu_display_price])

  // Calculate nutrition defaults based on category and serving
  const calculatedNutrition = useMemo(() => {
    if (!newItem.serving_size || !newItem.category_id || parseFloat(newItem.serving_size) <= 0) return null
    const category = categories.find(c => c.id === newItem.category_id)
    if (!category) return null
    if (hasCustomNutrition(newItem.nutrition)) return null
    return calculateNutritionDefaults(category.name, parseFloat(newItem.serving_size), newItem.serving_unit)
  }, [newItem.serving_size, newItem.serving_unit, newItem.category_id, newItem.nutrition, categories])

  // Auto-calculate platform price for edit mode
  useEffect(() => {
    if (editingItem && editingItem.actual_price && parseFloat(editingItem.actual_price) > 0) {
      const platformPrice = calculatePlatformPrice(parseFloat(editingItem.actual_price), 2)
      setEditingItem(prev => ({ ...prev, platform_price: platformPrice }))
    }
  }, [editingItem?.actual_price])

  // Calculate pricing breakdown for edit mode
  const editPricingBreakdown = useMemo(() => {
    if (!editingItem || !editingItem.actual_price || parseFloat(editingItem.actual_price) <= 0) return null
    return getPricingBreakdown(
      editingItem.actual_price,
      editingItem.platform_price,
      editingItem.menu_display_price,
      2
    )
  }, [editingItem?.actual_price, editingItem?.platform_price, editingItem?.menu_display_price])

  // Calculate nutrition defaults for edit mode
  const editCalculatedNutrition = useMemo(() => {
    if (!editingItem || !editingItem.serving_size || !editingItem.category_id || parseFloat(editingItem.serving_size) <= 0) return null
    const category = categories.find(c => c.id === editingItem.category_id)
    if (!category) return null
    
    // Check if user has custom nutrition
    const hasCustom = editingItem.nutritional_info && (
      editingItem.nutritional_info.calories > 0 ||
      editingItem.nutritional_info.protein > 0 ||
      editingItem.nutritional_info.fat > 0 ||
      editingItem.nutritional_info.carbs > 0 ||
      editingItem.nutritional_info.fiber > 0
    )
    if (hasCustom) return null
    
    return calculateNutritionDefaults(category.name, parseFloat(editingItem.serving_size), editingItem.serving_unit)
  }, [editingItem?.serving_size, editingItem?.serving_unit, editingItem?.category_id, editingItem?.nutritional_info, categories])

  // Helper functions for ingredients
  const handleAddIngredient = () => {
    const trimmedInput = ingredientInput.trim()
    
    // Support comma-separated ingredients
    if (trimmedInput.includes(',')) {
      const ingredientsList = trimmedInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0 && !newItem.ingredients.includes(item))
      
      if (ingredientsList.length > 0) {
        setNewItem(prev => ({
          ...prev,
          ingredients: [...prev.ingredients, ...ingredientsList]
        }))
        setIngredientInput('')
      }
    } else if (trimmedInput && !newItem.ingredients.includes(trimmedInput)) {
      // Single ingredient
      setNewItem(prev => ({
        ...prev,
        ingredients: [...prev.ingredients, trimmedInput]
      }))
      setIngredientInput('')
    }
  }

  const handleRemoveIngredient = (index) => {
    setNewItem(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }))
  }

  const handleIngredientKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddIngredient()
    }
  }

  const handleAllergenToggle = (allergen) => {
    setNewItem(prev => ({
      ...prev,
      allergens: prev.allergens.includes(allergen)
        ? prev.allergens.filter(a => a !== allergen)
        : [...prev.allergens, allergen]
    }))
  }

  // Edit mode handlers
  const handleEditAddIngredient = () => {
    const trimmedInput = editIngredientInput.trim()
    
    // Support comma-separated ingredients
    if (trimmedInput.includes(',')) {
      const ingredientsList = trimmedInput
        .split(',')
        .map(item => item.trim())
        .filter(item => item.length > 0 && !editingItem.ingredients?.includes(item))
      
      if (ingredientsList.length > 0) {
        setEditingItem(prev => ({
          ...prev,
          ingredients: [...(prev.ingredients || []), ...ingredientsList]
        }))
        setEditIngredientInput('')
      }
    } else if (trimmedInput && !editingItem.ingredients?.includes(trimmedInput)) {
      setEditingItem(prev => ({
        ...prev,
        ingredients: [...(prev.ingredients || []), trimmedInput]
      }))
      setEditIngredientInput('')
    }
  }

  const handleEditRemoveIngredient = (index) => {
    setEditingItem(prev => ({
      ...prev,
      ingredients: (prev.ingredients || []).filter((_, i) => i !== index)
    }))
  }

  const handleEditIngredientKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleEditAddIngredient()
    }
  }

  const handleEditAllergenToggle = (allergen) => {
    setEditingItem(prev => ({
      ...prev,
      allergens: (prev.allergens || []).includes(allergen)
        ? (prev.allergens || []).filter(a => a !== allergen)
        : [...(prev.allergens || []), allergen]
    }))
  }

  const handleAddItem = async () => {
    // Validation
    const pricingValidation = validatePricing(
      newItem.actual_price,
      newItem.platform_price,
      newItem.menu_display_price
    )
    
    const servingValidation = validateServing(
      newItem.serving_size,
      newItem.serving_unit
    )
    
    const errors = [
      ...pricingValidation.errors,
      ...servingValidation.errors
    ]
    
    if (!newItem.name || newItem.name.trim().length < 3) {
      errors.push('Item name must be at least 3 characters')
    }
    
    if (!newItem.category_id) {
      errors.push('Please select a category')
    }
    
    if (errors.length > 0) {
      setValidationErrors(errors)
      toast.error(errors[0])
      return
    }
    
    setValidationErrors([])
    if (newItem.name && newItem.actual_price && newItem.category_id && newItem.serving_size) {
      setUploadingImage(true)
      let imageUrl = newItem.image_url
      
      
      try {
        // Handle image upload if provided
        if (selectedImage) {
          // Compress image first for menu items (outside try blocks for scope)
          const compressedImage = await compressRestaurantImage(selectedImage, 'menu-item')
          
          try {
            // Try RLS-FREE upload first (guaranteed to work)
            console.log('🚀 Attempting RLS-free upload...')
            const uploadResult = await uploadImageRLSFree(
              compressedImage,
              restaurantId,
              'menu-item'
            )
            
            if (!uploadResult || !uploadResult.url) {
              throw new Error('Upload succeeded but no URL returned')
            }
            
            imageUrl = uploadResult.url
            toast.success('Image uploaded successfully!')
            console.log('✅ RLS-free upload successful:', uploadResult.url)
          } catch (uploadError) {
            console.error('❌ RLS-free upload failed, trying fallback...', uploadError)
            
            // FALLBACK: Try original upload method
            try {
              const fallbackResult = await uploadRestaurantImage(
                compressedImage,
                restaurantId,
                'menu-item'
              )
              
              imageUrl = fallbackResult.url
              toast.success('Image uploaded successfully (fallback method)!')
              console.log('✅ Fallback upload successful:', fallbackResult.url)
            } catch (fallbackError) {
              console.error('❌ Both upload methods failed:', fallbackError)
              toast.error('Failed to upload image: ' + fallbackError.message)
              throw fallbackError
            }
          }
        }
        
        // Prepare nutritional info
        let nutritionalInfo = null
        let nutritionAutoCalculated = false
        
        if (hasCustomNutrition(newItem.nutrition)) {
          // User provided custom nutrition
          nutritionalInfo = {
            calories: parseFloat(newItem.nutrition.calories) || 0,
            protein: parseFloat(newItem.nutrition.protein) || 0,
            fat: parseFloat(newItem.nutrition.fat) || 0,
            carbs: parseFloat(newItem.nutrition.carbs) || 0,
            fiber: parseFloat(newItem.nutrition.fiber) || 0,
            per_serving: true,
            auto_calculated: false
          }
          nutritionAutoCalculated = false
        } else if (calculatedNutrition) {
          // Use auto-calculated nutrition
          nutritionalInfo = calculatedNutrition
          nutritionAutoCalculated = true
        }
        
        // Create the complete item data
        const itemData = {
          name: newItem.name.trim(),
          description: newItem.description?.trim() || null,
          actual_price: parseFloat(newItem.actual_price),
          platform_price: parseFloat(newItem.platform_price),
          menu_display_price: newItem.menu_display_price ? parseFloat(newItem.menu_display_price) : null,
          price: parseFloat(newItem.platform_price), // Keep for backward compatibility
          category_id: newItem.category_id,
          serving_size: parseFloat(newItem.serving_size),
          serving_unit: newItem.serving_unit,
          preparation_time: newItem.preparation_time ? parseInt(newItem.preparation_time) : 15,
          allergens: newItem.allergens.length > 0 ? newItem.allergens : [],
          ingredients: newItem.ingredients.length > 0 ? newItem.ingredients : [],
          nutritional_info: nutritionalInfo,
          nutrition_auto_calculated: nutritionAutoCalculated,
          is_vegetarian: newItem.is_vegetarian,
          is_vegan: newItem.is_vegan,
          is_spicy: newItem.is_spicy,
          is_available: newItem.is_available,
          image_url: imageUrl || null
        }
        
        // Validate that if an image was selected, we got a URL
        if (selectedImage && !imageUrl) {
          throw new Error('Image upload failed - no URL received')
        }
        
        if (!onAddItem) {
          throw new Error('onAddItem function not provided')
        }
        
        await onAddItem(itemData)
        
        // Reset form
        setNewItem({
          name: '',
          description: '',
          actual_price: '',
          platform_price: '',
          menu_display_price: '',
          category_id: '',
          serving_size: '',
          serving_unit: 'g',
          preparation_time: '15',
          allergens: [],
          ingredients: [],
          is_vegetarian: false,
          is_vegan: false,
          is_spicy: false,
          nutrition: {
            calories: '',
            protein: '',
            fat: '',
            carbs: '',
            fiber: ''
          },
          is_available: true,
          image_url: ''
        })
        setSelectedImage(null)
        setImagePreview('')
        setShowAddItemModal(false)
        setValidationErrors([])
        
      } catch (error) {
        console.error('Error adding menu item:', error)
        toast.error('Failed to add menu item')
      } finally {
        setUploadingImage(false)
      }
    }
  }

  const handleAddCategory = () => {
    if (newCategory.name) {
      const category = {
        // Remove the id field - let the database generate UUID automatically
        ...newCategory,
        sort_order: categories.length + 1
      }
      onAddCategory?.(category)
      setNewCategory({ name: '', description: '' })
      setShowAddCategoryModal(false)
    }
  }

  const handleUpdateItem = async (itemId, updates) => {
    let finalUpdates = { ...updates }
    
    // Remove nested objects and non-updatable fields
    const {
      id,
      categories,
      created_at,
      updated_at,
      restaurant_id,
      ...cleanUpdates
    } = finalUpdates
    
    finalUpdates = cleanUpdates
    
    
    // Upload image if file is selected for editing
    if (editSelectedImage) {
      setUploadingImage(true)
      try {
        // Compress image first for menu items
        const compressedImage = await compressRestaurantImage(editSelectedImage, 'menu-item')
        
        // Upload with proper restaurant organization
        const uploadResult = await uploadRestaurantImage(
          compressedImage,
          restaurantId,
          'menu-item'
        )
        finalUpdates.image_url = uploadResult.url
        toast.success('Image uploaded successfully!')
      } catch (error) {
        console.error('Error uploading image:', error)
        toast.error('Failed to upload image')
        setUploadingImage(false)
        return
      }
      setUploadingImage(false)
    }
    
    onUpdateItem?.(itemId, finalUpdates)
  }

  const handleDeleteItem = async (itemId) => {
    const confirmed = await showConfirmation({
      title: 'Delete Menu Item',
      message: 'Are you sure you want to delete this menu item?\n\nThis action cannot be undone.',
      type: 'warning',
      confirmText: 'Delete Item',
      cancelText: 'Cancel',
      confirmButtonColor: 'red'
    })
    
    if (confirmed) {
      onDeleteItem?.(itemId)
    }
  }

  // Image handling functions
  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelectedImage(file)
      setNewItem({ ...newItem, image_url: '' }) // Clear URL when file is selected
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setImagePreview(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const clearImageSelection = () => {
    setSelectedImage(null)
    setImagePreview('')
    // Reset file input
    const fileInput = document.getElementById('image-upload')
    if (fileInput) fileInput.value = ''
  }

  const handleEditImageSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setEditSelectedImage(file)
      
      // Create preview
      const reader = new FileReader()
      reader.onload = (e) => {
        setEditImagePreview(e.target.result)
        setEditingItem({ ...editingItem, image_url: '' }) // Clear URL when file is selected
      }
      reader.readAsDataURL(file)
    }
  }

  const clearEditImageSelection = () => {
    setEditSelectedImage(null)
    setEditImagePreview('')
    // Reset file input
    const fileInput = document.getElementById('edit-image-upload')
    if (fileInput) fileInput.value = ''
  }

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        setSelectedImage(file)
        setNewItem({ ...newItem, image_url: '' })
        
        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
          setImagePreview(e.target.result)
        }
        reader.readAsDataURL(file)
      } else {
        toast.error('Please drop an image file')
      }
    }
  }

  // Edit drag and drop handlers
  const handleEditDragOver = (e) => {
    e.preventDefault()
    setIsEditDragging(true)
  }

  const handleEditDragLeave = (e) => {
    e.preventDefault()
    setIsEditDragging(false)
  }

  const handleEditDrop = (e) => {
    e.preventDefault()
    setIsEditDragging(false)
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        setEditSelectedImage(file)
        
        // Create preview
        const reader = new FileReader()
        reader.onload = (e) => {
          setEditImagePreview(e.target.result)
          setEditingItem({ ...editingItem, image_url: '' })
        }
        reader.readAsDataURL(file)
      }
    }
  }

  return (
    <div className="space-y-12">
      {/* Premium Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10">
        <div>
          <h2 className="text-4xl font-black text-gray-900 tracking-tighter lowercase mb-4">
             <span className="text-orange-500 italic">/</span>menu management
          </h2>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
            {categories.length} categories / {menuItems.length} total dishes
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowAddCategoryModal(true)}
            className="flex items-center gap-3 px-6 py-4 bg-gray-50 text-gray-900 text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-gray-100 transition-all border border-gray-100"
          >
            <PlusIcon className="h-4 w-4 stroke-[3]" />
            New Category
          </button>
          
          <button
            onClick={() => setShowAddItemModal(true)}
            className="flex items-center gap-3 px-8 py-4 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-black transition-all shadow-xl shadow-gray-200"
          >
            <SparklesIcon className="h-4 w-4 stroke-[3]" />
            Add Dish
          </button>
        </div>
      </div>

      {/* Menu Categories */}
      <div className="space-y-20">
        {categories.length > 0 ? categories.map((category) => (
          <motion.div 
            key={category.id} 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative"
          >
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 border-b border-gray-50 pb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                   <div className="w-1.5 h-1.5 rounded-full bg-orange-500"></div>
                   <span className="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em]">{category.sort_order || '01'}</span>
                </div>
                <h3 className="text-2xl font-black text-gray-900 tracking-tight uppercase">{category.name}</h3>
                {category.description && (
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2 max-w-xl leading-relaxed italic">
                    {category.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-4 bg-gray-50/50 px-5 py-2.5 rounded-2xl border border-gray-100">
                <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                  {menuItems.filter(item => item.category_id === category.id).length} Items
                </span>
              </div>
            </div>
               {/* Premium Dish Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {menuItems
                .filter(item => item.category_id === category.id)
                .map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-[40px] border border-gray-100 p-6 hover:shadow-2xl hover:shadow-gray-200/20 transition-all duration-700 group relative overflow-hidden"
                  >
                    {/* Media Node */}
                    <div className="relative aspect-square rounded-[32px] overflow-hidden mb-6 bg-gray-50 border border-gray-100">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover transition-all duration-1000 group-hover:scale-110"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                          <PhotoIcon className="h-8 w-8 text-gray-200" />
                          <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">No Media</span>
                        </div>
                      )}
                      
                      {/* Intelligence Overlay */}
                      <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500 bg-gradient-to-t from-gray-900/80 to-transparent">
                         <div className="flex gap-2">
                            <button
                              onClick={() => setEditingItem(item)}
                              className="flex-1 py-3 bg-white/10 backdrop-blur-md rounded-2xl text-[10px] font-black text-white hover:bg-white hover:text-gray-900 transition-all uppercase tracking-widest"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="w-12 h-12 flex items-center justify-center bg-rose-500/20 backdrop-blur-md rounded-2xl text-rose-400 hover:bg-rose-500 hover:text-white transition-all"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                         </div>
                      </div>

                      {/* Status Node */}
                      <div className="absolute top-4 left-4">
                        <button
                          onClick={() => handleUpdateItem(item.id, { is_available: !item.is_available })}
                          className={`w-10 h-10 rounded-2xl flex items-center justify-center backdrop-blur-md transition-all duration-500 border ${
                            item.is_available 
                              ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500' 
                              : 'bg-rose-500/10 border-rose-500/20 text-rose-500'
                          }`}
                        >
                          {item.is_available ? (
                            <EyeIcon className="h-4 w-4" />
                          ) : (
                            <EyeSlashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Content Architecture */}
                    <div className="space-y-4">
                      <div className="flex items-start justify-between gap-4">
                        <h4 className="text-lg font-black text-gray-900 tracking-tight uppercase leading-tight line-clamp-2">
                          {item.name}
                        </h4>
                        <div className="bg-gray-50 border border-gray-100 rounded-xl px-3 py-1.5 self-start">
                           <span className="text-[10px] font-black text-gray-900 uppercase">₹{item.price}</span>
                        </div>
                      </div>
                      
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-relaxed line-clamp-2 italic">
                        {item.description || 'No description provided'}
                      </p>

                      <div className="pt-4 border-t border-gray-50 flex items-center justify-between">
                         <div className="flex items-center gap-2">
                            <ClockIcon className="h-3 w-3 text-gray-300" />
                            <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{item.preparation_time || '15'}m Prep</span>
                         </div>
                         {item.is_vegetarian && (
                            <div className="w-2.5 h-2.5 border border-emerald-500 p-0.5 rounded-sm">
                               <div className="w-full h-full bg-emerald-500 rounded-full"></div>
                            </div>
                         )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              
              {/* Add Unit Node */}
              <motion.div
                whileHover={{ scale: 0.98 }}
                onClick={() => setShowAddItemModal(true)}
                className="bg-gray-50 rounded-[40px] border-2 border-dashed border-gray-200 hover:border-gray-900 transition-all duration-500 cursor-pointer flex flex-col items-center justify-center min-h-[300px] group"
              >
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100 mb-6 transition-all duration-500 group-hover:bg-gray-900 group-hover:scale-110">
                  <PlusIcon className="h-6 w-6 text-gray-400 transition-all duration-500 group-hover:text-white" />
                </div>
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] group-hover:text-gray-900">Add New Dish</span>
              </motion.div>
            </div>
          </motion.div>
        )) : (
          <div className="bg-white rounded-[40px] p-24 border border-gray-100 text-center shadow-xl shadow-gray-100/50">
            <div className="w-20 h-20 bg-gray-50 rounded-[32px] flex items-center justify-center mx-auto mb-8 border border-gray-100">
              <PhotoIcon className="h-10 w-10 text-gray-300" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 tracking-tight uppercase">Menu is Empty</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-4 mb-10 max-w-xs mx-auto">Start by adding your first category and dishes to the menu.</p>
            <button
              onClick={() => setShowAddCategoryModal(true)}
              className="inline-flex items-center gap-4 px-10 py-5 bg-gray-900 text-white text-[10px] font-black uppercase tracking-widest rounded-2xl hover:bg-black transition-all shadow-xl shadow-gray-200"
            >
              <PlusIcon className="h-4 w-4 stroke-[3]" />
              Add First Category
            </button>
          </div>
        )}
      </div>

      {/* Add Item Modal */}
      {showAddItemModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[85vh] flex flex-col shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-neutral-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-bold text-neutral-900">Add Menu Item</h3>
                <button
                  onClick={() => setShowAddItemModal(false)}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 flex-1 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Item Name</label>
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="e.g., Butter Chicken"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Description</label>
                <textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                  placeholder="Describe your dish..."
                  rows={3}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Category <span className="text-red-500">*</span></label>
                <select
                  value={newItem.category_id}
                  onChange={(e) => setNewItem({ ...newItem, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>

              {/* Validation Errors */}
              {validationErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-red-800 mb-1">Please fix the following errors:</h4>
                  <ul className="list-disc list-inside text-xs text-red-700 space-y-1">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Pricing Strategy Section */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-neutral-900">Pricing & Payment Gateway Fee</h4>
                  <InformationCircleIcon className="h-4 w-4 text-orange-500" />
                </div>
                <p className="text-xs text-neutral-600 mb-3">
                  Set your base price. We'll add a 2% payment gateway fee.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Actual Price (₹) <span className="text-red-500">*</span>
                    <span className="ml-1 text-xs text-neutral-500">(Amount you receive)</span>
                  </label>
                  <input
                    type="number"
                    value={newItem.actual_price}
                    onChange={(e) => setNewItem({ ...newItem, actual_price: e.target.value })}
                    placeholder="0.00"
                    min="1"
                    step="0.01"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Platform Price (₹) 
                    <span className="ml-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">Auto</span>
                  </label>
                  <input
                    type="number"
                    value={newItem.platform_price}
                    readOnly
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-green-50 text-neutral-700 cursor-not-allowed"
                  />
                  {newItem.actual_price && newItem.platform_price && (
                    <p className="text-xs text-neutral-600 mt-1">
                      ₹{parseFloat(newItem.actual_price).toFixed(2)} + 
                      ₹{(parseFloat(newItem.platform_price) - parseFloat(newItem.actual_price)).toFixed(2)} (2% payment gateway fee)
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    Menu Display Price (₹) 
                    <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">Optional</span>
                  </label>
                  <input
                    type="number"
                    value={newItem.menu_display_price}
                    onChange={(e) => setNewItem({ ...newItem, menu_display_price: e.target.value })}
                    placeholder={newItem.platform_price ? `Default: ${calculateDefaultDisplayPrice(parseFloat(newItem.platform_price)).toFixed(2)}` : "Leave empty for 10% markup"}
                    min={newItem.platform_price || 0}
                    step="0.01"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                  <p className="text-xs text-neutral-500 mt-1">
                    Show a higher price with discount effect. Leave empty for smart pricing (ends in 9, 5-20% discount).
                  </p>
                </div>
                
                {pricingBreakdown && (
                  <div className="bg-white border border-neutral-200 rounded-lg p-3 mt-3">
                    <h5 className="text-xs font-semibold text-neutral-700 mb-2">Customer View Preview</h5>
                    <div className="flex items-center gap-2 mb-2">
                      {pricingBreakdown.showDiscount && (
                        <span className="text-sm text-neutral-500 line-through">
                          ₹{pricingBreakdown.menuDisplayPrice}
                        </span>
                      )}
                      <span className="text-lg font-bold text-orange-600">
                        ₹{pricingBreakdown.platformPrice}
                      </span>
                      {pricingBreakdown.showDiscount && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                          {pricingBreakdown.customerDiscountPercent}% OFF
                        </span>
                      )}
                    </div>
                    <div className="border-t border-neutral-200 pt-2 mt-2 space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-600">You receive:</span>
                        <span className="font-semibold text-green-600">₹{pricingBreakdown.restaurantReceives}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-600">Payment gateway fee:</span>
                        <span className="text-neutral-500">₹{pricingBreakdown.paymentGatewayFee}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Serving Information Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-neutral-900 mb-2">
                  Serving Size <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-neutral-600 mb-3">
                  Required for nutritional calculations and customer information
                </p>
                
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input
                      type="number"
                      value={newItem.serving_size}
                      onChange={(e) => setNewItem({ ...newItem, serving_size: e.target.value })}
                      placeholder="280"
                      min="1"
                      step="1"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div className="w-32">
                    <select
                      value={newItem.serving_unit}
                      onChange={(e) => setNewItem({ ...newItem, serving_unit: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      {SERVING_UNIT_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Dish Details Section - Expandable */}
              <div className="border border-neutral-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowDishDetails(!showDishDetails)}
                  className="w-full px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="h-5 w-5 text-orange-500" />
                    <span className="text-sm font-semibold text-neutral-900">Dish Information</span>
                    <span className="px-2 py-0.5 bg-neutral-200 text-neutral-600 text-xs rounded">Optional</span>
                  </div>
                  {showDishDetails ? (
                    <ChevronUpIcon className="h-5 w-5 text-neutral-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-neutral-500" />
                  )}
                </button>
                
                {showDishDetails && (
                  <div className="p-4 space-y-4 bg-white">
                    
                    {/* Allergen Information */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Allergen Information
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {ALLERGEN_OPTIONS.map(allergen => (
                          <label
                            key={allergen.value}
                            className={`flex items-center gap-2 px-3 py-2 border rounded-lg cursor-pointer transition-all ${
                              newItem.allergens.includes(allergen.value)
                                ? 'border-orange-500 bg-orange-50'
                                : 'border-neutral-300 hover:border-orange-300'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={newItem.allergens.includes(allergen.value)}
                              onChange={() => handleAllergenToggle(allergen.value)}
                              className="rounded border-neutral-300 text-orange-500 focus:ring-orange-500"
                            />
                            <span className="text-sm">{allergen.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    
                    {/* Key Ingredients */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Key Ingredients
                      </label>
                      <div className="space-y-2">
                        {newItem.ingredients.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {newItem.ingredients.map((ingredient, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full"
                              >
                                {ingredient}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveIngredient(index)}
                                  className="hover:text-orange-900"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <input
                          type="text"
                          value={ingredientInput}
                          onChange={(e) => setIngredientInput(e.target.value)}
                          onKeyDown={handleIngredientKeyDown}
                          placeholder="Type ingredients (comma-separated) and press Enter"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        />
                        <p className="text-xs text-neutral-500">
                          Example: Chicken, Butter, Cream, Tomato, Spices (press Enter to add all)
                        </p>
                      </div>
                    </div>
                    
                    {/* Nutritional Information */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Nutritional Information (per serving)
                        {calculatedNutrition && (
                          <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                            Auto-calculated
                          </span>
                        )}
                      </label>
                      <p className="text-xs text-neutral-500 mb-3">
                        Leave empty to use default values based on category and serving size
                      </p>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">
                            Calories (kcal)
                          </label>
                          <input
                            type="number"
                            value={newItem.nutrition.calories}
                            onChange={(e) => setNewItem({
                              ...newItem,
                              nutrition: { ...newItem.nutrition, calories: e.target.value }
                            })}
                            placeholder={calculatedNutrition ? calculatedNutrition.calories.toString() : "Auto"}
                            min="0"
                            step="1"
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">
                            Protein (g)
                          </label>
                          <input
                            type="number"
                            value={newItem.nutrition.protein}
                            onChange={(e) => setNewItem({
                              ...newItem,
                              nutrition: { ...newItem.nutrition, protein: e.target.value }
                            })}
                            placeholder={calculatedNutrition ? calculatedNutrition.protein.toString() : "Auto"}
                            min="0"
                            step="0.1"
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">
                            Fat (g)
                          </label>
                          <input
                            type="number"
                            value={newItem.nutrition.fat}
                            onChange={(e) => setNewItem({
                              ...newItem,
                              nutrition: { ...newItem.nutrition, fat: e.target.value }
                            })}
                            placeholder={calculatedNutrition ? calculatedNutrition.fat.toString() : "Auto"}
                            min="0"
                            step="0.1"
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">
                            Carbs (g)
                          </label>
                          <input
                            type="number"
                            value={newItem.nutrition.carbs}
                            onChange={(e) => setNewItem({
                              ...newItem,
                              nutrition: { ...newItem.nutrition, carbs: e.target.value }
                            })}
                            placeholder={calculatedNutrition ? calculatedNutrition.carbs.toString() : "Auto"}
                            min="0"
                            step="0.1"
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-neutral-600 mb-1">
                            Fiber (g)
                          </label>
                          <input
                            type="number"
                            value={newItem.nutrition.fiber}
                            onChange={(e) => setNewItem({
                              ...newItem,
                              nutrition: { ...newItem.nutrition, fiber: e.target.value }
                            })}
                            placeholder={calculatedNutrition ? calculatedNutrition.fiber.toString() : "Auto"}
                            min="0"
                            step="0.1"
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Preparation Time */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        <ClockIcon className="h-4 w-4 inline mr-1" />
                        Preparation Time (minutes)
                      </label>
                      <input
                        type="number"
                        value={newItem.preparation_time}
                        onChange={(e) => setNewItem({ ...newItem, preparation_time: e.target.value })}
                        placeholder="15 (default)"
                        min="5"
                        max="120"
                        step="1"
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Estimated time to prepare this dish (5-120 minutes)
                      </p>
                    </div>
                    
                  </div>
                )}
              </div>
              
              {/* Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Item Image</label>
                
                {/* Image Preview */}
                {(imagePreview || newItem.image_url) && (
                  <div className="mb-3">
                    <img
                      src={imagePreview || newItem.image_url}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg border border-neutral-300"
                    />
                  </div>
                )}
                
                {/* Upload Options */}
                <div className="space-y-3">
                  {/* File Upload with Drag & Drop */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Upload Image File</label>
                    <div
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`relative border-2 border-dashed rounded-lg transition-all duration-200 ${
                        isDragging 
                          ? 'border-orange-400 bg-orange-50' 
                          : 'border-neutral-300 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageSelect}
                        className="hidden"
                        id="image-upload"
                      />
                      <label
                        htmlFor="image-upload"
                        className="flex flex-col items-center justify-center gap-2 px-4 py-6 cursor-pointer"
                      >
                        <CloudArrowUpIcon className={`h-8 w-8 transition-colors ${
                          isDragging ? 'text-orange-500' : 'text-neutral-400'
                        }`} />
                        <div className="text-center">
                          <span className="text-sm font-medium text-neutral-700">
                            {selectedImage ? selectedImage.name : 'Drop image here or click to browse'}
                          </span>
                          <p className="text-xs text-neutral-500 mt-1">
                            Supports: JPG, PNG, GIF up to 5MB
                          </p>
                        </div>
                      </label>
                      {selectedImage && (
                        <button
                          type="button"
                          onClick={clearImageSelection}
                          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md text-neutral-400 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* URL Input */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Or paste image URL</label>
                    <input
                      type="url"
                      value={newItem.image_url}
                      onChange={(e) => {
                        setNewItem({ ...newItem, image_url: e.target.value })
                        if (e.target.value && !selectedImage) {
                          setImagePreview('')
                        }
                      }}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      disabled={selectedImage}
                    />
                  </div>
                </div>
              </div>
              
              {/* Dietary Flags */}
              <div className="border border-neutral-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-neutral-900 mb-3">
                  Dietary Information
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="vegetarian"
                      checked={newItem.is_vegetarian}
                      onChange={(e) => setNewItem({ ...newItem, is_vegetarian: e.target.checked })}
                      className="rounded border-neutral-300 text-green-500 focus:ring-green-500"
                    />
                    <label htmlFor="vegetarian" className="text-sm font-medium text-neutral-700 flex items-center gap-1">
                      <span className="text-green-600">🥬</span> Vegetarian
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="vegan"
                      checked={newItem.is_vegan}
                      onChange={(e) => setNewItem({ ...newItem, is_vegan: e.target.checked })}
                      className="rounded border-neutral-300 text-green-500 focus:ring-green-500"
                    />
                    <label htmlFor="vegan" className="text-sm font-medium text-neutral-700 flex items-center gap-1">
                      <span className="text-green-600">🌱</span> Vegan
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="spicy"
                      checked={newItem.is_spicy}
                      onChange={(e) => setNewItem({ ...newItem, is_spicy: e.target.checked })}
                      className="rounded border-neutral-300 text-red-500 focus:ring-red-500"
                    />
                    <label htmlFor="spicy" className="text-sm font-medium text-neutral-700 flex items-center gap-1">
                      <span className="text-red-600">🌶️</span> Spicy
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="available"
                  checked={newItem.is_available}
                  onChange={(e) => setNewItem({ ...newItem, is_available: e.target.checked })}
                  className="rounded border-neutral-300 text-orange-500 focus:ring-orange-500"
                />
                <label htmlFor="available" className="text-sm font-medium text-neutral-700">
                  Available for order
                </label>
              </div>
              
            </div>
            
            <div className="p-4 sm:p-6 border-t border-neutral-200 bg-neutral-50 flex-shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddItemModal(false)}
                  className="flex-1 px-4 py-2 text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddItem}
                  disabled={uploadingImage}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploadingImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : (
                    'Add Item'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[85vh] flex flex-col shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-neutral-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-bold text-neutral-900">Add Category</h3>
                <button
                  onClick={() => setShowAddCategoryModal(false)}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 flex-1 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Category Name</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., Main Course"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Description (Optional)</label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  placeholder="Describe this category..."
                  rows={2}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div className="p-4 sm:p-6 border-t border-neutral-200 bg-neutral-50 flex-shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddCategoryModal(false)}
                  className="flex-1 px-4 py-2 text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddCategory}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                >
                  Add Category
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Item Modal - COMPREHENSIVE VERSION */}
      {editingItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl">
            <div className="p-4 sm:p-6 border-b border-neutral-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-bold text-neutral-900">Edit Menu Item</h3>
                <button
                  onClick={() => {
                    setEditingItem(null)
                    setEditIngredientInput('')
                    setEditShowDishDetails(false)
                  }}
                  className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 flex-1 overflow-y-auto">
              {/* Basic Information */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Item Name *</label>
                  <input
                    type="text"
                    value={editingItem.name || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                    placeholder="e.g., Butter Chicken"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Description</label>
                  <textarea
                    value={editingItem.description || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                    placeholder="Describe your dish..."
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Category *</label>
                  <select
                    value={editingItem.category_id || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Pricing Strategy */}
              <div className="border border-neutral-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-neutral-900 mb-3">
                  Pricing Strategy
                </label>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Actual Price (₹) * <span className="text-neutral-500">(What you receive)</span>
                    </label>
                    <input
                      type="number"
                      value={editingItem.actual_price || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, actual_price: e.target.value })}
                      min="0"
                      step="0.01"
                      placeholder="100"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Platform Price (₹) <span className="text-neutral-500">(Auto-calculated: +2% payment gateway fee)</span>
                    </label>
                    <input
                      type="number"
                      value={editingItem.platform_price || ''}
                      readOnly
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg bg-neutral-50 text-neutral-600"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Menu Display Price (₹) <span className="text-neutral-500">(Optional)</span>
                    </label>
                    <input
                      type="number"
                      value={editingItem.menu_display_price || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, menu_display_price: e.target.value })}
                      min="0"
                      step="0.01"
                      placeholder={editingItem.platform_price ? calculateDefaultDisplayPrice(parseFloat(editingItem.platform_price)).toString() : ''}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                    <p className="text-xs text-neutral-500 mt-1">
                      Show a higher price with discount effect. Leave empty for smart pricing (ends in 9, 5-20% discount).
                    </p>
                  </div>
                </div>

                {editPricingBreakdown && (
                  <div className="bg-white border border-neutral-200 rounded-lg p-3 mt-3">
                    <h5 className="text-xs font-semibold text-neutral-700 mb-2">Customer View Preview</h5>
                    <div className="flex items-center gap-2 mb-2">
                      {editPricingBreakdown.showDiscount && (
                        <span className="text-sm text-neutral-500 line-through">
                          ₹{editPricingBreakdown.menuDisplayPrice}
                        </span>
                      )}
                      <span className="text-lg font-bold text-orange-600">
                        ₹{editPricingBreakdown.platformPrice}
                      </span>
                      {editPricingBreakdown.showDiscount && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded">
                          {editPricingBreakdown.discountPercent}% OFF
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-neutral-600 space-y-1">
                      <div className="flex justify-between">
                        <span>You receive:</span>
                        <span className="font-semibold">₹{editPricingBreakdown.actualPrice}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Payment gateway fee (2%):</span>
                        <span className="font-semibold">₹{editPricingBreakdown.paymentGatewayFee}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Serving Information */}
              <div className="border border-neutral-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-neutral-900 mb-3">
                  Serving Information
                </label>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Serving Size *</label>
                    <input
                      type="number"
                      value={editingItem.serving_size || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, serving_size: e.target.value })}
                      min="0"
                      step="1"
                      placeholder="250"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Unit *</label>
                    <select
                      value={editingItem.serving_unit || 'g'}
                      onChange={(e) => setEditingItem({ ...editingItem, serving_unit: e.target.value })}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="g">grams (g)</option>
                      <option value="ml">milliliters (ml)</option>
                      <option value="piece">piece</option>
                      <option value="plate">plate</option>
                      <option value="bowl">bowl</option>
                      <option value="cup">cup</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="block text-xs font-medium text-neutral-600 mb-1">
                    Preparation Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={editingItem.preparation_time || '15'}
                    onChange={(e) => setEditingItem({ ...editingItem, preparation_time: e.target.value })}
                    min="1"
                    step="1"
                    placeholder="15"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Dish Details - Collapsible */}
              <div className="border border-neutral-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setEditShowDishDetails(!editShowDishDetails)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-neutral-50 transition-colors rounded-lg"
                >
                  <span className="text-sm font-semibold text-neutral-900">Dish Details</span>
                  <ChevronDownIcon className={`h-5 w-5 text-neutral-500 transition-transform ${editShowDishDetails ? 'rotate-180' : ''}`} />
                </button>

                {editShowDishDetails && (
                  <div className="px-4 pb-4 space-y-4">
                    {/* Allergens */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Allergen Information
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {['dairy', 'eggs', 'fish', 'shellfish', 'tree_nuts', 'peanuts', 'wheat', 'soy'].map(allergen => (
                          <label key={allergen} className="flex items-center gap-2 p-2 border border-neutral-200 rounded-lg hover:bg-neutral-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={(editingItem.allergens || []).includes(allergen)}
                              onChange={() => handleEditAllergenToggle(allergen)}
                              className="rounded border-neutral-300 text-orange-500 focus:ring-orange-500"
                            />
                            <span className="text-sm text-neutral-700 capitalize">
                              {allergen.replace('_', ' ')}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Key Ingredients */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Key Ingredients
                      </label>
                      <div className="space-y-2">
                        {(editingItem.ingredients || []).length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(editingItem.ingredients || []).map((ingredient, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full"
                              >
                                {ingredient}
                                <button
                                  type="button"
                                  onClick={() => handleEditRemoveIngredient(index)}
                                  className="hover:text-orange-900"
                                >
                                  <XMarkIcon className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        
                        <input
                          type="text"
                          value={editIngredientInput}
                          onChange={(e) => setEditIngredientInput(e.target.value)}
                          onKeyDown={handleEditIngredientKeyDown}
                          placeholder="Type ingredients (comma-separated) and press Enter"
                          className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        />
                        <p className="text-xs text-neutral-500">
                          Example: Chicken, Butter, Cream, Tomato, Spices (press Enter to add all)
                        </p>
                      </div>
                    </div>

                    {/* Nutritional Information - Simplified for now */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Nutritional Information (per serving)
                      </label>
                      <p className="text-xs text-neutral-500 mb-2">
                        Basic nutrition fields - full implementation coming
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Dietary Flags */}
              <div className="border border-neutral-200 rounded-lg p-4">
                <label className="block text-sm font-semibold text-neutral-900 mb-3">
                  Dietary Information
                </label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-vegetarian"
                      checked={editingItem.is_vegetarian || false}
                      onChange={(e) => setEditingItem({ ...editingItem, is_vegetarian: e.target.checked })}
                      className="rounded border-neutral-300 text-green-500 focus:ring-green-500"
                    />
                    <label htmlFor="edit-vegetarian" className="text-sm font-medium text-neutral-700 flex items-center gap-1">
                      <span className="text-green-600">🥬</span> Vegetarian
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-vegan"
                      checked={editingItem.is_vegan || false}
                      onChange={(e) => setEditingItem({ ...editingItem, is_vegan: e.target.checked })}
                      className="rounded border-neutral-300 text-green-500 focus:ring-green-500"
                    />
                    <label htmlFor="edit-vegan" className="text-sm font-medium text-neutral-700 flex items-center gap-1">
                      <span className="text-green-600">🌱</span> Vegan
                    </label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-spicy"
                      checked={editingItem.is_spicy || false}
                      onChange={(e) => setEditingItem({ ...editingItem, is_spicy: e.target.checked })}
                      className="rounded border-neutral-300 text-red-500 focus:ring-red-500"
                    />
                    <label htmlFor="edit-spicy" className="text-sm font-medium text-neutral-700 flex items-center gap-1">
                      <span className="text-red-600">🌶️</span> Spicy
                    </label>
                  </div>
                </div>
              </div>
              
              {/* Edit Image Upload Section */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">Item Image</label>
                
                {/* Image Preview */}
                {(editImagePreview || editingItem.image_url) && (
                  <div className="mb-3">
                    <img
                      src={editImagePreview || editingItem.image_url}
                      alt="Preview"
                      className="w-full h-32 object-cover rounded-lg border border-neutral-300"
                    />
                  </div>
                )}
                
                {/* Upload Options */}
                <div className="space-y-3">
                  {/* File Upload with Drag & Drop */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Upload New Image</label>
                    <div
                      onDragOver={handleEditDragOver}
                      onDragLeave={handleEditDragLeave}
                      onDrop={handleEditDrop}
                      className={`relative border-2 border-dashed rounded-lg transition-all duration-200 ${
                        isEditDragging 
                          ? 'border-orange-400 bg-orange-50' 
                          : 'border-neutral-300 hover:border-orange-300 hover:bg-orange-50'
                      }`}
                    >
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleEditImageSelect}
                        className="hidden"
                        id="edit-image-upload"
                      />
                      <label
                        htmlFor="edit-image-upload"
                        className="flex flex-col items-center justify-center gap-2 px-4 py-6 cursor-pointer"
                      >
                        <CloudArrowUpIcon className={`h-8 w-8 transition-colors ${
                          isEditDragging ? 'text-orange-500' : 'text-neutral-400'
                        }`} />
                        <div className="text-center">
                          <span className="text-sm font-medium text-neutral-700">
                            {editSelectedImage ? editSelectedImage.name : 'Drop image here or click to browse'}
                          </span>
                          <p className="text-xs text-neutral-500 mt-1">
                            Supports: JPG, PNG, GIF up to 5MB
                          </p>
                        </div>
                      </label>
                      {editSelectedImage && (
                        <button
                          type="button"
                          onClick={clearEditImageSelection}
                          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md text-neutral-400 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* URL Input */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">Or paste image URL</label>
                    <input
                      type="url"
                      value={editingItem.image_url || ''}
                      onChange={(e) => {
                        setEditingItem({ ...editingItem, image_url: e.target.value })
                        if (e.target.value && !editSelectedImage) {
                          setEditImagePreview('')
                        }
                      }}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      disabled={editSelectedImage}
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="edit-available"
                  checked={editingItem.is_available}
                  onChange={(e) => setEditingItem({ ...editingItem, is_available: e.target.checked })}
                  className="rounded border-neutral-300 text-orange-500 focus:ring-orange-500"
                />
                <label htmlFor="edit-available" className="text-sm font-medium text-neutral-700">
                  Available for order
                </label>
              </div>
              
            </div>
            
            <div className="p-4 sm:p-6 border-t border-neutral-200 bg-neutral-50 flex-shrink-0">
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setEditingItem(null)
                    setEditIngredientInput('')
                    setEditShowDishDetails(false)
                    setEditSelectedImage(null)
                    setEditImagePreview('')
                  }}
                  className="flex-1 px-4 py-2 text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    // Recalculate all auto-generated fields before saving
                    const platformPrice = editingItem.actual_price 
                      ? calculatePlatformPrice(parseFloat(editingItem.actual_price), 4)
                      : editingItem.platform_price
                    
                    // Auto-generate menu_display_price if empty
                    const menuDisplayPrice = editingItem.menu_display_price && parseFloat(editingItem.menu_display_price) > 0
                      ? parseFloat(editingItem.menu_display_price)
                      : calculateDefaultDisplayPrice(platformPrice)
                    
                    const updatedItem = {
                      ...editingItem,
                      platform_price: platformPrice,
                      price: platformPrice, // For backward compatibility
                      menu_display_price: menuDisplayPrice
                    }
                    
                    await onUpdateItem(editingItem.id, updatedItem)
                    setEditingItem(null)
                    setEditIngredientInput('')
                    setEditShowDishDetails(false)
                    setEditSelectedImage(null)
                    setEditImagePreview('')
                  }}
                  disabled={uploadingImage}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploadingImage ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Uploading...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MenuTab
